/**
 * soccerPanels.ts — the SHARED truncated-icosahedron panel painter (GS2, the
 * graphics-realism overhaul).
 *
 * One per-pixel spherical-Voronoi pass computes the TRUE soccer-ball layout —
 * 12 pentagons + 20 hexagons as the Voronoi cells of the 32 panel-centre
 * directions (12 icosahedron vertices = pentagons, 20 dodecahedron vertices =
 * hexagon centres) — with the angular distance to the nearest seam per pixel.
 * Because everything is ANGULAR (radians on the unit sphere), the result is
 * resolution-independent and free of the old equirect "ninja-star" polar
 * stretching: a pentagon near the pole paints correctly by construction.
 *
 * TWO consumers ride the one scan:
 *  - the TITLE GLOBE (Globe.ts, M72/M82b): `soccerPanelMaps()` — the exact
 *    overlay/seams/bump trio the globe has always worn (math moved here
 *    VERBATIM; the module-level cache still means one ~120 ms pass per
 *    session shared by the title stage, tour spinner and every flight);
 *  - the MATCH BALL (BallView.ts, GS2): `ballPanelMaps()` — a colour-NEUTRAL
 *    near-white leather set (albedo + stitch bump + roughness/scuff) that
 *    each racer's ball TINTS via material.color, so ONE 512×256 set serves
 *    all 7–8 balls on track at zero per-ball cost.
 *
 * The pixel work lives in PURE functions (no DOM) — `computeGlobePanelPixels`
 * / `computeBallPanelPixels` — so the geometry is unit-testable under the
 * node vitest environment; the exported map builders are thin cached canvas
 * wrappers around them.
 */

/** How many of the 32 panel centres are pentagons (they come FIRST). */
export const PENTAGON_COUNT = 12;

/** The 32 panel-centre directions (flat xyz triplets, normalized): 12
 *  icosahedron vertices (pentagons) then 20 dodecahedron vertices (hexagons).
 *  Cached — every scan shares one table. */
let centersCache: number[] | null = null;
export function panelCenters(): number[] {
  if (centersCache) return centersCache;
  const PHI = (1 + Math.sqrt(5)) / 2;
  const centers: number[] = [];
  const push = (x: number, y: number, z: number): void => {
    const l = Math.hypot(x, y, z);
    centers.push(x / l, y / l, z / l);
  };
  // 12 icosahedron vertices — the pentagon centres (cyclic permutations of (0, ±1, ±φ)).
  for (const s1 of [1, -1])
    for (const s2 of [1, -1]) {
      push(0, s1, s2 * PHI);
      push(s1, s2 * PHI, 0);
      push(s2 * PHI, 0, s1);
    }
  // 20 dodecahedron vertices — the hexagon centres.
  for (const sx of [1, -1]) for (const sy of [1, -1]) for (const sz of [1, -1]) push(sx, sy, sz);
  for (const s1 of [1, -1])
    for (const s2 of [1, -1]) {
      push(0, s1 / PHI, s2 * PHI);
      push(s1 / PHI, s2 * PHI, 0);
      push(s2 * PHI, 0, s1 / PHI);
    }
  centersCache = centers;
  return centers;
}

/**
 * M158: the TRUE match-ball layout. `panelCenters()` (kept byte-for-byte for
 * the title globe's owner-locked look) pairs the icosahedron with a dodecahedron
 * in the wrong cyclic order — measured: one "hexagon" centre sits 10.8° from a
 * pentagon, so its cells are slivers and the old ball read blotchy. The real
 * dual puts the hexagon centres at the icosahedron's FACE centres: (±1,±1,±1)
 * plus cyclic permutations of (±1/φ, 0, ±φ). Every pentagon then has exactly
 * five hexagon neighbours at 37.4° and every hexagon three pentagons + three
 * hexagons (test-pinned).
 */
let ballCentersCache: number[] | null = null;
export function ballPanelCenters(): number[] {
  if (ballCentersCache) return ballCentersCache;
  const PHI = (1 + Math.sqrt(5)) / 2;
  const out = panelCenters().slice(0, PENTAGON_COUNT * 3);
  const push = (x: number, y: number, z: number): void => {
    const l = Math.hypot(x, y, z);
    out.push(x / l, y / l, z / l);
  };
  for (const sx of [1, -1]) for (const sy of [1, -1]) for (const sz of [1, -1]) push(sx, sy, sz);
  for (const s1 of [1, -1])
    for (const s2 of [1, -1]) {
      push(s1 / PHI, 0, s2 * PHI);
      push(0, s2 * PHI, s1 / PHI);
      push(s2 * PHI, s1 / PHI, 0);
    }
  ballCentersCache = out;
  return out;
}

/**
 * The per-pixel Voronoi scan every painter shares. For each equirect pixel it
 * finds the nearest + second-nearest panel centres and the angular distance
 * `b` to the seam between them (0 exactly ON a seam), then hands the painter
 * callback everything colour work needs:
 *   o     — the pixel's byte offset into a W×H RGBA buffer,
 *   b     — angular distance (radians) to the panel boundary,
 *   best  — nearest panel index (< PENTAGON_COUNT ⇒ a pentagon),
 *   i2    — second-nearest panel index (the seam's other panel),
 *   vx/vy/vz — the pixel's unit direction (for along-seam stitch phase).
 *
 * The lat/lon → direction mapping and the d1/d2 bookkeeping are byte-for-byte
 * the M72 globe pass, so the globe's cached skins are unchanged by GS2.
 */
function scanPanels(
  W: number,
  H: number,
  table: number[],
  paint: (
    o: number,
    b: number,
    best: number,
    i2: number,
    vx: number,
    vy: number,
    vz: number,
  ) => void,
): void {
  const centers = table;
  const n = centers.length / 3;
  for (let py = 0; py < H; py++) {
    const lat = (0.5 - (py + 0.5) / H) * Math.PI; // +π/2 top → -π/2 bottom
    const cosLa = Math.cos(lat);
    const sinLa = Math.sin(lat);
    for (let px = 0; px < W; px++) {
      const lon = ((px + 0.5) / W) * Math.PI * 2 - Math.PI;
      const vx = cosLa * Math.sin(lon);
      const vy = sinLa;
      const vz = cosLa * Math.cos(lon);
      let d1 = -2;
      let d2 = -2;
      let best = 0;
      let i2 = 0;
      for (let i = 0; i < n; i++) {
        const d = vx * centers[i * 3] + vy * centers[i * 3 + 1] + vz * centers[i * 3 + 2];
        if (d > d1) {
          d2 = d1;
          i2 = best;
          d1 = d;
          best = i;
        } else if (d > d2) {
          d2 = d;
          i2 = i;
        }
      }
      // Angular distance to the panel boundary (0 exactly on a seam).
      const b =
        (Math.acos(Math.max(-1, Math.min(1, d2))) - Math.acos(Math.max(-1, Math.min(1, d1)))) / 2;
      paint((py * W + px) * 4, b, best, i2, vx, vy, vz);
    }
  }
}

// ------------------------------------------------------------------ globe

/** The globe pass paints at this size (matches Globe.ts's TEX_W/TEX_H — its
 *  skins drawImage these maps onto same-sized canvases). */
export const GLOBE_PANEL_W = 1024;
export const GLOBE_PANEL_H = 512;

/**
 * PURE pixel pass for the title globe's panel maps — the M72 math VERBATIM
 * (seam 0.014 rad, bevel 0.075, dark pentagons on the overlay, the M82b
 * bronze seams-only layer, recessed-stitch bump). Returns raw RGBA buffers.
 */
export function computeGlobePanelPixels(
  W: number,
  H: number,
): {
  overlay: Uint8ClampedArray<ArrayBuffer>;
  seams: Uint8ClampedArray<ArrayBuffer>;
  bump: Uint8ClampedArray<ArrayBuffer>;
} {
  const overlay = new Uint8ClampedArray(W * H * 4);
  const seams = new Uint8ClampedArray(W * H * 4);
  const bump = new Uint8ClampedArray(W * H * 4);
  const SEAM = 0.014; // seam half-width (radians) — thin stitched groove
  const BEVEL = 0.075; // soft panel-edge shading band (radians)
  scanPanels(W, H, panelCenters(), (o, b, best) => {
    const seamT = Math.max(0, 1 - b / SEAM); // 1 on the seam → 0 off it
    const seam = seamT * seamT * (3 - 2 * seamT);
    const bevel = Math.max(0, 1 - b / BEVEL) * 0.16; // soft quilted edge shade
    const pentagon = best < PENTAGON_COUNT ? 0.3 : 0; // classic dark pentagons
    // Overlay: near-black stitching + panel tints (alpha carries strength).
    const alpha = Math.min(1, seam * 0.85 + bevel + pentagon);
    overlay[o] = 6;
    overlay[o + 1] = 8;
    overlay[o + 2] = 12;
    overlay[o + 3] = Math.round(alpha * 255);
    // Seams-only pass (M82b): stitching + bevel in warm BRONZE, no pentagon
    // tint — the green-and-gold cup ball carries no black or white.
    seams[o] = 96;
    seams[o + 1] = 68;
    seams[o + 2] = 14;
    seams[o + 3] = Math.round(Math.min(1, seam * 0.95 + bevel * 0.8) * 255);
    // Bump: recessed seams, gentle dome toward each panel centre.
    const dome = Math.min(1, b / 0.16) * 34;
    const g = Math.max(0, Math.min(255, 150 + dome - seam * 105 - bevel * 90));
    bump[o] = g;
    bump[o + 1] = g;
    bump[o + 2] = g;
    bump[o + 3] = 255;
  });
  return { overlay, seams, bump };
}

let globeMapsCache: {
  overlay: HTMLCanvasElement;
  /** M82b: stitching + bevel ONLY (no dark pentagons), coloured warm bronze —
   *  the cup ball is green + gold with NO black or white (owner rule). */
  seams: HTMLCanvasElement;
  bump: HTMLCanvasElement;
} | null = null;

/**
 * The REAL soccer-ball panel layout for the TITLE GLOBE (M72): overlay
 * (tint/seam, drawn over albedo + emissive), the M82b bronze seams-only
 * layer, and a bump map. ~120 ms on desktop (several × on a phone), so the
 * result is CACHED for the session — the intro spinner and every tour flight
 * share one computation. (Extracted from Globe.ts in GS2 so the match ball
 * shares the Voronoi pass; output is unchanged.)
 */
export function soccerPanelMaps(): {
  overlay: HTMLCanvasElement;
  seams: HTMLCanvasElement;
  bump: HTMLCanvasElement;
} {
  if (globeMapsCache) return globeMapsCache;
  const px = computeGlobePanelPixels(GLOBE_PANEL_W, GLOBE_PANEL_H);
  globeMapsCache = {
    overlay: toCanvas(px.overlay, GLOBE_PANEL_W, GLOBE_PANEL_H),
    seams: toCanvas(px.seams, GLOBE_PANEL_W, GLOBE_PANEL_H),
    bump: toCanvas(px.bump, GLOBE_PANEL_W, GLOBE_PANEL_H),
  };
  return globeMapsCache;
}

// ------------------------------------------------------------------- ball

/**
 * MATCH-BALL map size. 768×384 measured ~35 ms desktop — still cheaper than
 * the globe's pass, built lazily at the first BallView construction (inside a
 * multi-second city load, never mid-race) and cached for the session. It was
 * A/B'd against 512×256 in the GS2 macro shots: 512 aliased the stitch bars
 * into chunky zipper teeth at camera-a-few-units range; 768 resolves them as
 * fine thread ticks. Knob: cost scales linearly with pixel count.
 */
export const BALL_PANEL_W = 1024; // M158: 768 → 1024 (finer stitch ticks + leather pebble on the raised panels)
export const BALL_PANEL_H = 512;

// ------------------------------------------------------------- ball mesh (M158)

/** Rings from a panel's centre (t = 0) to its seam (t = 1), as fractions of
 *  the angle centre → perimeter point. The last three rings are the rounded
 *  shoulder dropping into the groove. */
const PANEL_RINGS = [0.4, 0.66, 0.82, 0.91, 0.965, 1];
/** Segments per panel side along the seam. */
const PANEL_SIDE_SEGS = 3;
/** How far the seam floor sits below the panel crown (unit radius). Exaggerated
 *  past a real ball (~1.5%) so the grooves read from the chase camera. */
export const BALL_SEAM_DEPTH = 0.045;

/** Radius of a raised panel at ring fraction t: a gentle crown (peak exactly
 *  1 = the physics radius, so the ball still rests ON the road), then a
 *  rounded shoulder into the seam. */
export function panelProfile(t: number): number {
  if (t <= 0.82) return 1 - 0.016 * t * t;
  const s = (t - 0.82) / 0.18; // 0 at the shoulder, 1 in the seam
  const shoulderTop = 1 - 0.016 * 0.82 * 0.82;
  const floor = 1 - BALL_SEAM_DEPTH;
  return shoulderTop + (floor - shoulderTop) * s * s; // flat at the crown edge, steep into the seam
}

function slerp(a: number[], b: number[], t: number): number[] {
  const d = Math.max(-1, Math.min(1, a[0] * b[0] + a[1] * b[1] + a[2] * b[2]));
  const om = Math.acos(d);
  if (om < 1e-6) return [a[0], a[1], a[2]];
  const s = Math.sin(om);
  const ka = Math.sin((1 - t) * om) / s;
  const kb = Math.sin(t * om) / s;
  return [a[0] * ka + b[0] * kb, a[1] * ka + b[1] * kb, a[2] * ka + b[2] * kb];
}

/**
 * The MATCH BALL as real geometry (M158, the owner's "classic panels,
 * upgraded"): 32 separately domed panels whose borders are EXACTLY the
 * texture's Voronoi seams, meeting in a V groove `BALL_SEAM_DEPTH` deep. Each
 * panel keeps its own copy of its border vertices, so the groove floor is a
 * crisp crease while every crown shades smooth. Unit radius, indexed,
 * outward winding; ≈5.9k triangles. Pure (no three.js) so it is unit-tested.
 */
export function ballPanelMesh(): { positions: Float32Array; indices: Uint32Array } {
  const C = ballPanelCenters();
  const n = C.length / 3;
  const ctr = (i: number): number[] => [C[i * 3], C[i * 3 + 1], C[i * 3 + 2]];
  const pos: number[] = [];
  const idx: number[] = [];
  for (let a = 0; a < n; a++) {
    const A = ctr(a);
    const sides = a < PENTAGON_COUNT ? 5 : 6;
    // Neighbours = the nearest `sides` centres, ordered around A.
    const near = [...Array(n).keys()]
      .filter((i) => i !== a)
      .map((i) => ({ i, d: A[0] * C[i * 3] + A[1] * C[i * 3 + 1] + A[2] * C[i * 3 + 2] }))
      .sort((p, q) => q.d - p.d)
      .slice(0, sides)
      .map((p) => p.i);
    // Tangent frame at A for the angular sort.
    const up = Math.abs(A[1]) < 0.9 ? [0, 1, 0] : [1, 0, 0];
    const e1 = norm(cross(up, A));
    const e2 = cross(A, e1);
    near.sort((p, q) => {
      const P = ctr(p);
      const Q = ctr(q);
      return Math.atan2(dot(P, e2), dot(P, e1)) - Math.atan2(dot(Q, e2), dot(Q, e1));
    });
    // Voronoi corners: equidistant from A and two consecutive neighbours.
    const corners = near.map((b, k) => {
      const B = ctr(b);
      const Cn = ctr(near[(k + 1) % sides]);
      let p = norm(cross(sub(B, A), sub(Cn, A)));
      if (dot(p, A) < 0) p = [-p[0], -p[1], -p[2]];
      return p;
    });
    // Perimeter along the great-circle seams.
    const perim: number[][] = [];
    for (let k = 0; k < sides; k++) {
      for (let s = 0; s < PANEL_SIDE_SEGS; s++) {
        perim.push(norm(slerp(corners[k], corners[(k + 1) % sides], s / PANEL_SIDE_SEGS)));
      }
    }
    const P = perim.length;
    const base = pos.length / 3;
    pos.push(A[0], A[1], A[2]); // crown
    for (const t of PANEL_RINGS) {
      const rr = panelProfile(t);
      for (const q of perim) {
        const d = norm(slerp(A, q, t));
        pos.push(d[0] * rr, d[1] * rr, d[2] * rr);
      }
    }
    const ring = (r: number, j: number): number => base + 1 + r * P + (j % P);
    // Winding check on the first fan triangle: flip the panel if it faces in.
    const v0 = [pos[base * 3], pos[base * 3 + 1], pos[base * 3 + 2]];
    const v1 = pos.slice(ring(0, 0) * 3, ring(0, 0) * 3 + 3);
    const v2 = pos.slice(ring(0, 1) * 3, ring(0, 1) * 3 + 3);
    const flip = dot(cross(sub(v1, v0), sub(v2, v0)), A) < 0;
    const tri = (x: number, y: number, z: number): void => {
      if (flip) idx.push(x, z, y);
      else idx.push(x, y, z);
    };
    for (let j = 0; j < P; j++) tri(base, ring(0, j), ring(0, j + 1));
    for (let r = 0; r < PANEL_RINGS.length - 1; r++) {
      for (let j = 0; j < P; j++) {
        tri(ring(r, j), ring(r + 1, j), ring(r + 1, j + 1));
        tri(ring(r, j), ring(r + 1, j + 1), ring(r, j + 1));
      }
    }
  }
  return { positions: new Float32Array(pos), indices: new Uint32Array(idx) };
}

function dot(a: number[], b: number[]): number {
  return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
}
function cross(a: number[], b: number[]): number[] {
  return [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
}
function sub(a: number[], b: number[]): number[] {
  return [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
}
function norm(a: number[]): number[] {
  const l = Math.hypot(a[0], a[1], a[2]);
  return [a[0] / l, a[1] / l, a[2] / l];
}

// ---- the ball's look knobs (angular values are radians on the sphere) ----
const BALL_SEAM = 0.02; // seam groove half-width — wider than the globe's so it survives mips at race distance
const BALL_BEVEL = 0.1; // quilted panel-edge shading band
const STITCH_PITCH = 0.062; // one thread bar per this many radians along a seam
const STITCH_BAND = 0.03; // thread bars reach this far either side of the seam
const PANEL_VALUE = 236; // near-white leather (material.color tints it per racer)
const PENTAGON_VALUE = 30; // classic dark pentagons (tint keeps them in the racer's hue)
const ROUGH_BASE = 107; // ≈0.42 leather roughness (green channel; material scalar stays 1)

/** Deterministic 2-int hash → [0,1). No Math.random anywhere — the maps are
 *  byte-reproducible (test-pinned) and identical on every client. */
function hash01(x: number, y = 0): number {
  let h = (Math.imul(x, 374761393) + Math.imul(y, 668265263)) | 0;
  h = (h ^ (h >>> 13)) | 0;
  h = Math.imul(h, 1274126177);
  h = (h ^ (h >>> 16)) >>> 0;
  return h / 4294967296;
}

/** Smooth value noise on a cell lattice, wrapped horizontally so the equirect
 *  x=0 seam never shows a hard edge. Returns [0,1). */
function valueNoise(px: number, py: number, cell: number, cellsX: number, seed: number): number {
  const x = px / cell;
  const y = py / cell;
  let gx = Math.floor(x);
  const gy = Math.floor(y);
  const fx = x - gx;
  const fy = y - gy;
  gx = ((gx % cellsX) + cellsX) % cellsX;
  const gx1 = (gx + 1) % cellsX;
  const sx = fx * fx * (3 - 2 * fx);
  const sy = fy * fy * (3 - 2 * fy);
  const v00 = hash01(gx + seed * 7919, gy);
  const v10 = hash01(gx1 + seed * 7919, gy);
  const v01 = hash01(gx + seed * 7919, gy + 1);
  const v11 = hash01(gx1 + seed * 7919, gy + 1);
  const top = v00 + (v10 - v00) * sx;
  const bot = v01 + (v11 - v01) * sx;
  return top + (bot - top) * sy;
}

/**
 * PURE pixel pass for the MATCH BALL's texture set (GS2). Colour-neutral by
 * design — the racer's identity colour arrives via material.color, so:
 *  - albedo: near-white leather panels (per-panel value jitter + soft mottle),
 *    AO'd bevels, dark grooved seams with visible thread bars, near-black
 *    pentagons. Tinting white-over-near-white still reads (the WHITE ball's
 *    panels survive via the dark pentagons + seams).
 *  - bump: panel domes, recessed seam grooves, thread bars proud INSIDE the
 *    groove (the stitching read at macro), fine leather grain.
 *  - rough: leather base ≈0.42 with matte seams/threads, a soft sheen mottle,
 *    and sparse micro-SCUFF speckle clusters (matte marks that break the
 *    specular at macro without reading at race distance).
 */
export function computeBallPanelPixels(
  W: number,
  H: number,
): {
  albedo: Uint8ClampedArray<ArrayBuffer>;
  bump: Uint8ClampedArray<ArrayBuffer>;
  rough: Uint8ClampedArray<ArrayBuffer>;
} {
  const albedo = new Uint8ClampedArray(W * H * 4);
  const bump = new Uint8ClampedArray(W * H * 4);
  const rough = new Uint8ClampedArray(W * H * 4);
  const centers = ballPanelCenters();
  const mottleCellsX = Math.max(1, Math.round(W / 16)); // ~16px mottle lattice, wrapped
  const scuffCellsX = Math.max(1, Math.round(W / 3)); // ~3px scuff cells → small blobs, not 1px dust
  scanPanels(W, H, centers, (o, b, best, i2, vx, vy, vz) => {
    const px = (o / 4) % W;
    const py = (o / 4 - px) / W;

    const seamT = Math.max(0, 1 - b / BALL_SEAM);
    const seam = seamT * seamT * (3 - 2 * seamT); // smooth groove profile
    const bevelT = Math.max(0, 1 - b / BALL_BEVEL);
    const bevel = bevelT * bevelT * (3 - 2 * bevelT);
    const pentagon = best < PENTAGON_COUNT;

    // ---- stitching: short thread bars crossing the seam at a regular pitch.
    // Phase runs ALONG the seam: order the panel pair deterministically, build
    // an in-plane frame (u1 toward the seam midpoint, u2 along the seam), and
    // read the angle of this pixel's direction in that frame. Only computed
    // inside the thread band (~6% of pixels).
    let stitch = 0;
    if (b < STITCH_BAND) {
      const lo = Math.min(best, i2) * 3;
      const hi = Math.max(best, i2) * 3;
      let ux = centers[lo] + centers[hi];
      let uy = centers[lo + 1] + centers[hi + 1];
      let uz = centers[lo + 2] + centers[hi + 2];
      const ul = Math.hypot(ux, uy, uz);
      ux /= ul;
      uy /= ul;
      uz /= ul;
      let nx = centers[lo] - centers[hi];
      let ny = centers[lo + 1] - centers[hi + 1];
      let nz = centers[lo + 2] - centers[hi + 2];
      const nl = Math.hypot(nx, ny, nz);
      nx /= nl;
      ny /= nl;
      nz /= nl;
      const wx = ny * uz - nz * uy; // u2 = n × u1 (unit — n ⊥ u1)
      const wy = nz * ux - nx * uz;
      const wz = nx * uy - ny * ux;
      const s = Math.atan2(vx * wx + vy * wy + vz * wz, vx * ux + vy * uy + vz * uz);
      const f = s / STITCH_PITCH - Math.floor(s / STITCH_PITCH); // 0..1 phase
      // A thread bar occupies the first 0.36 of each pitch, feathered at both
      // ends, and fades toward the band edge (bars taper, not chop).
      const bar = Math.max(0, Math.min(1, Math.min(f, 0.36 - f) / 0.06));
      const reach = Math.max(0, 1 - b / STITCH_BAND);
      stitch = bar * reach * reach;
    }

    // ---- albedo (neutral gray — material.color supplies the racer hue) ----
    const panelJitter = 1 + (hash01(best, 61) * 2 - 1) * 0.05; // per-panel patchwork
    const mottle = (valueNoise(px, py, 16, mottleCellsX, 3) - 0.5) * 8; // soft leather mottle
    const grain = (hash01(px, py) - 0.5) * 5; // fine per-pixel grain
    let v = pentagon ? PENTAGON_VALUE : PANEL_VALUE * panelJitter + mottle + grain;
    v *= 1 - bevel * 0.16; // quilted AO toward every edge
    v *= 1 - seam * 0.52; // the groove floor sits in shadow
    v += stitch * seam * 30; // thread catches a little light inside the groove
    albedo[o] = v;
    albedo[o + 1] = v;
    albedo[o + 2] = v;
    albedo[o + 3] = 255;

    // ---- bump (150-neutral, like the globe's) ----
    const dome = Math.min(1, b / 0.16) * 26; // gentle per-panel dome
    const bg =
      150 + dome - seam * 100 - bevel * 34 + stitch * seam * 52 + (hash01(px, py + 7717) - 0.5) * 6;
    bump[o] = bg;
    bump[o + 1] = bg;
    bump[o + 2] = bg;
    bump[o + 3] = 255;

    // ---- roughness (green channel is what three.js reads) ----
    const sheen = (valueNoise(px, py, 12, Math.max(1, Math.round(W / 12)), 11) - 0.5) * 16;
    // Sparse micro-scuffs: ~1.6% of 3px cells go matte (a scuffed patch of
    // leather) — enough to break the macro specular, invisible at distance.
    const scuffCell = valueNoise(px, py, 3, scuffCellsX, 23);
    const scuff = scuffCell > 0.984 ? 40 + hash01(px >> 2, py >> 2) * 30 : 0;
    const rg = ROUGH_BASE + sheen + scuff + seam * 56 + stitch * seam * 22 + bevel * 10;
    rough[o] = rg;
    rough[o + 1] = rg;
    rough[o + 2] = rg;
    rough[o + 3] = 255;
  });
  return { albedo, bump, rough };
}

let ballMapsCache: {
  albedo: HTMLCanvasElement;
  bump: HTMLCanvasElement;
  rough: HTMLCanvasElement;
} | null = null;

/**
 * The match ball's canvases, computed ONCE per session (module-level cache —
 * every BallView across every race shares this one set; ~30 ms desktop,
 * paid inside the first city load).
 */
export function ballPanelMaps(): {
  albedo: HTMLCanvasElement;
  bump: HTMLCanvasElement;
  rough: HTMLCanvasElement;
} {
  if (ballMapsCache) return ballMapsCache;
  const px = computeBallPanelPixels(BALL_PANEL_W, BALL_PANEL_H);
  ballMapsCache = {
    albedo: toCanvas(px.albedo, BALL_PANEL_W, BALL_PANEL_H),
    bump: toCanvas(px.bump, BALL_PANEL_W, BALL_PANEL_H),
    rough: toCanvas(px.rough, BALL_PANEL_W, BALL_PANEL_H),
  };
  return ballMapsCache;
}

// ---------------------------------------------------------------- helpers

function toCanvas(pixels: Uint8ClampedArray<ArrayBuffer>, W: number, H: number): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = W;
  c.height = H;
  const g = c.getContext('2d');
  if (!g) throw new Error('Could not get 2D context for soccer panel maps');
  g.putImageData(new ImageData(pixels, W, H), 0, 0);
  return c;
}
